import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import {
  type HelpAnnounceOptions,
  type HelpArticleEntity,
  type HelpMediaEntity,
} from "@fitness-app/data-models/entities/HelpArticle";
import { parseHelpContent, type HelpContent } from "@fitness-app/data-models/entities/HelpContent";
import { type Database, type Json } from "@fitness-app/supabase";

import { getClientWebApiBaseUrl } from "~/modules/Sales/SalesAgent/constants";
import { analyticsService, notificationsApi, supabase, useAppSelector } from "~/store/initializeStore";
import { type HelpArticleRowInsert } from "./helpArticleForm";
import { helpMediaKind, posterFromVideo, prepareHelpMedia, type PreparedHelpMedia } from "./helpMediaFile";

export const HELP_ARTICLES_KEY = ["help_article"] as const;
export const HELP_MEDIA_KEY = ["help_media"] as const;
export const HELP_SNIPPETS_KEY = ["help_snippet"] as const;
export const HELP_MEDIA_BUCKET = "help-media";

const reportError = (error: unknown, fallback: string) => {
  const detail = error instanceof Error ? error.message : "";
  void message.error(detail ? `${fallback} ${detail}` : fallback);
};

/**
 * Wywołanie endpointu client-weba tokenem zalogowanej osoby. Bez wspólnego
 * sekretu: panel jest aplikacją w przeglądarce, więc sekret w jego bundle'u
 * byłby publiczny.
 */
const callClientWeb = async <T>(path: string, body: unknown): Promise<T> => {
  // Ten sam adres, którym panel woła resztę tras client-weba (agent sprzedaży,
  // wsparcie, Studio) — osobna zmienna znaczyłaby kolejny wpis w trzech
  // workflowach budujących panel i pustą wartość w każdym, o którym ktoś zapomni.
  const clientWebOrigin = getClientWebApiBaseUrl();
  if (!clientWebOrigin) {
    throw new Error("Brak adresu client-weba w konfiguracji panelu.");
  }

  const { data } = await supabase.auth.getSession();
  const response = await fetch(`${clientWebOrigin}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${data.session?.access_token ?? ""}` },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const detail = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(detail?.error ?? `Odpowiedź ${response.status}`);
  }

  return (await response.json()) as T;
};

export const helpArticlePaths = (article: Pick<HelpArticleEntity, "kind" | "slug">) => [
  "/pomoc",
  ...(article.kind === "update"
    ? ["/pomoc/co-nowego", `/pomoc/co-nowego/${article.slug}`]
    : [`/pomoc/${article.slug}`]),
];

export const useHelpPreviewLink = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { path } = await callClientWeb<{ path: string }>("/api/help/preview-link", { id });
      analyticsService.track("help_preview_link_created");
      return `${getClientWebApiBaseUrl()}${path}`;
    },
    onError: (error) => reportError(error, "Nie udało się przygotować podglądu."),
  });

export type HelpSnippetEntity = Database["public"]["Tables"]["help_snippet"]["Row"];

export interface HelpSnippet {
  id: string;
  name: string;
  description: string;
  content: HelpContent;
}

/**
 * Szablony bloków. Uwaga „Korzystaj we własnym tempie” czy czterokrokowy wstęp
 * wracają w kolejnych materiałach — zamiast przepisywać je ręcznie, redakcja
 * zapisuje raz i wstawia z paska albo ukośnikiem.
 */
export const useHelpSnippets = () =>
  useQuery({
    queryKey: HELP_SNIPPETS_KEY,
    queryFn: async (): Promise<HelpSnippet[]> => {
      const { data, error } = await supabase.from("help_snippet").select("*").order("name");

      if (error) {
        throw error;
      }

      return (data as HelpSnippetEntity[]).map((snippet) => ({
        id: snippet.id,
        name: snippet.name,
        description: snippet.description,
        // Szablon zapisany starszą wersją edytora nie może wywalić menu —
        // nieznany węzeł po prostu odpada.
        content: parseHelpContent(snippet.content),
      }));
    },
  });

export const useSaveHelpSnippet = () => {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();

  return useMutation({
    mutationFn: async ({ name, description, content }: { name: string; description: string; content: HelpContent }) => {
      const { error } = await supabase
        .from("help_snippet")
        .upsert({ name, description, content: content as unknown as Json, createdBy: userId }, { onConflict: "name" });

      if (error) {
        throw error;
      }
    },
    onSuccess: async () => {
      void message.success("Szablon zapisany.");
      analyticsService.track("help_snippet_saved");
      await queryClient.invalidateQueries({ queryKey: HELP_SNIPPETS_KEY });
    },
    onError: (error) => reportError(error, "Nie udało się zapisać szablonu."),
  });
};

export const useDeleteHelpSnippet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("help_snippet").delete().eq("id", id);

      if (error) {
        throw error;
      }
    },
    onSuccess: async () => {
      void message.success("Szablon usunięty.");
      await queryClient.invalidateQueries({ queryKey: HELP_SNIPPETS_KEY });
    },
    onError: (error) => reportError(error, "Nie udało się usunąć szablonu."),
  });
};

export const useCurrentUserId = () => useAppSelector((state) => state.user.data?.id ?? null);

export const useHelpArticles = () =>
  useQuery({
    queryKey: HELP_ARTICLES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.from("help_article").select("*");

      if (error) {
        throw error;
      }

      return data as HelpArticleEntity[];
    },
  });

export const useHelpMedia = () =>
  useQuery({
    queryKey: HELP_MEDIA_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.from("help_media").select("*").order("createdAt", { ascending: false });

      if (error) {
        throw error;
      }

      return data as HelpMediaEntity[];
    },
  });

export const useSaveHelpArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, row }: { id: string | null; row: HelpArticleRowInsert }) => {
      const query = id
        ? supabase.from("help_article").update(row).eq("id", id).select().single()
        : supabase.from("help_article").insert(row).select().single();
      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data as HelpArticleEntity;
    },
    onSuccess: async (article, variables) => {
      void message.success("Zapisano materiał.");
      // Zdarzenia lecą po SUKCESIE — nieudany zapis nie jest redakcją.
      analyticsService.track("help_article_saved", {
        kind: article.kind,
        slug: article.slug,
        status: article.status,
        is_new: !variables.id,
      });
      await queryClient.invalidateQueries({ queryKey: HELP_ARTICLES_KEY });
    },
    onError: (error) => reportError(error, "Nie udało się zapisać materiału."),
  });
};

/**
 * Ogłoszenie wpisu klientkom. Jedna funkcja bazodanowa zamiast kilku zapisów —
 * patrz migracja `20260913120000_help_announcement_review_fixes`.
 */
const announceHelpArticle = async (
  articleId: string,
  announce: HelpAnnounceOptions,
  // Publikacja razem z ogłoszeniem — w jednej transakcji, żeby nieudane
  // ogłoszenie nie zostawiło materiału u klientek bez popupu i z błędem w panelu.
  { publish }: { publish: boolean },
) => {
  const { error } = await supabase.rpc("help_announce_article", {
    p_article_id: articleId,
    p_cta_text: announce.ctaText,
    p_visible_days: announce.visibleForDays,
    p_product_ids: announce.target.productIds,
    p_product_types: announce.target.productTypes,
    p_tags: announce.target.tags,
    // Tagi bez warunku znaczą „bez zawężenia”, więc baza odbija taki zapis —
    // panel pilnuje tego już w formularzu.
    p_tags_condition: announce.target.tags.length ? announce.target.tagsCondition : null,
    p_publish: publish,
  });

  if (error) {
    throw error;
  }
};

/**
 * Publikacja i jej cofnięcie. Osobno od zapisu metadanych, bo to inna decyzja
 * i inny klucz uprawnienia — `helpCenter.publish`. Bez klucza baza i tak odbije
 * zapis, a przycisk w panelu w ogóle się nie pokaże.
 */
export const useChangeHelpArticleStatus = () => {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      article,
      announce,
    }: {
      id: string;
      status: HelpArticleEntity["status"];
      article: HelpArticleEntity;
      /**
       * Ogłoszenie klientkom — popup, a opcjonalnie też push. Osobno od samej
       * publikacji, bo to osobna decyzja: materiał bywa publikowany zanim
       * wydanie dojdzie do sklepów, a poprawka w opublikowanym wpisie nie ma
       * prawa wysłać popupu drugi raz.
       */
      announce?: HelpAnnounceOptions | null;
    }) => {
      // Jedna zmienna niosąca „ogłaszamy” ORAZ opcje — inaczej każdy odczyt
      // opcji niżej wymaga powtórzenia tego samego warunku.
      const announcement = status === "published" && announce?.announce ? announce : null;

      if (announcement) {
        // Publikacja i ogłoszenie JEDNYM wywołaniem: kryteria, daty, tekst
        // przycisku, wyzerowanie znacznika pusha, skasowanie śladu „widziane”
        // i samo przejście do `published` wchodzą razem albo wcale. Osobny
        // zapis statusu zostawiał materiał u klientek bez ogłoszenia, gdy
        // drugie wywołanie się nie udało.
        await announceHelpArticle(id, announcement, { publish: true });
      } else {
        const { error } = await supabase
          .from("help_article")
          .update({
            status,
            // Cofnięcie publikacji zdejmuje datę: materiał i jego media znikają
            // klientkom od razu, bez czekania na wdrożenie client-weba.
            publishedAt: status === "published" ? new Date().toISOString() : null,
            updatedBy: userId,
          })
          .eq("id", id);

        if (error) {
          throw error;
        }
      }

      if (announcement?.push) {
        // Push jest osobnym wywołaniem i osobnym ryzykiem: nieudany nie może
        // cofnąć ogłoszenia, które już siedzi w bazie — popup i tak dojdzie.
        try {
          await notificationsApi.post("/help/announce-push", {
            articleId: id,
            audience: announcement.pushAudience,
          });
        } catch (pushError) {
          void message.warning(
            `Ogłoszenie zapisane, ale push nie poszedł: ${
              pushError instanceof Error ? pushError.message : "nieznany błąd"
            }`,
          );
        }
      }

      // Regeneracja stron pomocy: bez niej klientka zobaczyłaby zmianę dopiero
      // po wygaśnięciu ISR albo po wdrożeniu client-weba. Nieudana rewalidacja
      // nie może cofnąć publikacji, która już siedzi w bazie — stąd ostrzeżenie
      // zamiast wyjątku.
      try {
        await callClientWeb("/api/revalidate", { paths: helpArticlePaths(article) });
      } catch (revalidateError) {
        void message.warning(
          `Zapisano status, ale strony pomocy nie odświeżyły się automatycznie: ${
            revalidateError instanceof Error ? revalidateError.message : "nieznany błąd"
          }`,
        );
      }
    },
    onSuccess: async (_result, variables) => {
      const announced = variables.status === "published" && variables.announce?.announce;
      void message.success(
        announced
          ? "Materiał opublikowany i ogłoszony klientkom."
          : variables.status === "published"
            ? "Materiał opublikowany."
            : "Zmieniono status.",
      );
      analyticsService.track(variables.status === "published" ? "help_article_published" : "help_article_unpublished", {
        kind: variables.article.kind,
        slug: variables.article.slug,
        status: variables.status,
      });
      if (announced) {
        void queryClient.invalidateQueries({ queryKey: HELP_ANNOUNCEMENT_TARGET_KEY });
        analyticsService.track("help_article_announced", {
          slug: variables.article.slug,
          push: Boolean(variables.announce?.push),
          pushAudience: variables.announce?.pushAudience,
          visibleForDays: variables.announce?.visibleForDays,
        });
      }
      await queryClient.invalidateQueries({ queryKey: HELP_ARTICLES_KEY });
    },
    onError: (error) => reportError(error, "Nie udało się zmienić statusu."),
  });
};

export const useReorderHelpGuides = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Cała kolejność jednym wywołaniem — nie zapytanie na kartę.
    mutationFn: async (orderedIds: string[]) => {
      const { error } = await supabase.rpc("help_reorder_guides", { p_ids: orderedIds });

      if (error) {
        throw error;
      }
    },
    onSuccess: async (_result, orderedIds) => {
      analyticsService.track("help_guides_reordered", { count: orderedIds.length });
      await queryClient.invalidateQueries({ queryKey: HELP_ARTICLES_KEY });
    },
    onError: (error) => reportError(error, "Nie udało się zapisać kolejności."),
  });
};

export const useDeleteHelpArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("help_article").delete().eq("id", id);

      if (error) {
        throw error;
      }
    },
    onSuccess: async () => {
      void message.success("Materiał usunięty.");
      analyticsService.track("help_article_deleted");
      await queryClient.invalidateQueries({ queryKey: HELP_ARTICLES_KEY });
    },
    onError: (error) => reportError(error, "Nie udało się usunąć materiału."),
  });
};

export const useUploadHelpMedia = () => {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();

  const store = async (prepared: PreparedHelpMedia) => {
    const { error: uploadError } = await supabase.storage
      .from(HELP_MEDIA_BUCKET)
      .upload(prepared.storagePath, prepared.file, { contentType: prepared.mimeType, upsert: true });

    if (uploadError) {
      throw uploadError;
    }

    // Rejestr jest allowlistą dla endpointu mediów w client-webie: plik,
    // którego tu nie ma, nie zostanie zserwowany klientce.
    const { error } = await supabase.from("help_media").upsert(
      {
        storagePath: prepared.storagePath,
        mimeType: prepared.mimeType,
        bytes: prepared.bytes,
        width: prepared.width,
        height: prepared.height,
        durationSeconds: prepared.durationSeconds,
        checksum: prepared.checksum,
        createdBy: userId,
      },
      { onConflict: "storagePath" },
    );

    if (error) {
      throw error;
    }
  };

  return useMutation({
    mutationFn: async (file: File): Promise<PreparedHelpMedia & { posterPath?: string }> => {
      const prepared = await prepareHelpMedia(file);
      await store(prepared);

      if (helpMediaKind(prepared.mimeType) !== "video") {
        return prepared;
      }

      // Plakat wycinamy tylko wtedy, gdy nagranie jeszcze żadnego nie ma:
      // przy powtórnym wgraniu tego samego pliku plakat wybrany świadomie
      // przez redakcję ma zostać (nazwa pliku jest hashem, więc to ten sam wiersz).
      const { data: existing, error: lookupError } = await supabase
        .from("help_media")
        .select("posterPath")
        .eq("storagePath", prepared.storagePath)
        .maybeSingle();
      // Błąd odczytu wygląda tak samo jak brak plakatu, a to dwie różne rzeczy:
      // po cichu wyciętą pierwszą klatką nadpisalibyśmy plakat wybrany świadomie.
      if (lookupError) {
        throw lookupError;
      }
      if (existing?.posterPath) {
        return { ...prepared, posterPath: existing.posterPath };
      }

      const poster = await posterFromVideo(file);
      await store(poster);
      const { error } = await supabase
        .from("help_media")
        .update({ posterPath: poster.storagePath })
        .eq("storagePath", prepared.storagePath);
      if (error) {
        throw error;
      }

      return { ...prepared, posterPath: poster.storagePath };
    },
    onSuccess: async (prepared) => {
      void message.success(`Wgrano ${prepared.storagePath}.`);
      analyticsService.track("help_media_uploaded", {
        mime_type: prepared.mimeType,
        bytes: prepared.bytes,
        has_dimensions: prepared.width !== null,
      });
      await queryClient.invalidateQueries({ queryKey: HELP_MEDIA_KEY });
    },
    onError: (error) => reportError(error, "Nie udało się wgrać pliku."),
  });
};

export const useDeleteHelpMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (media: HelpMediaEntity) => {
      const { error: storageError } = await supabase.storage.from(HELP_MEDIA_BUCKET).remove([media.storagePath]);

      if (storageError) {
        throw storageError;
      }

      const { error } = await supabase.from("help_media").delete().eq("id", media.id);

      if (error) {
        throw error;
      }
    },
    onSuccess: async () => {
      void message.success("Plik usunięty.");
      analyticsService.track("help_media_deleted");
      await queryClient.invalidateQueries({ queryKey: HELP_MEDIA_KEY });
    },
    onError: (error) => reportError(error, "Nie udało się usunąć pliku."),
  });
};

/**
 * Podpisane adresy dla całej listy jednym wywołaniem — bucket jest prywatny,
 * a picker bez miniatur pokazuje wyłącznie nazwy z hasha, czyli nic.
 */
export const useHelpMediaThumbnails = (storagePaths: string[]) => {
  const key = [...storagePaths].sort().join(",");

  return useQuery({
    queryKey: ["help_media_thumbnails", key],
    enabled: storagePaths.length > 0,
    staleTime: 1000 * 60 * 30,
    queryFn: async () => {
      const { data, error } = await supabase.storage.from(HELP_MEDIA_BUCKET).createSignedUrls(storagePaths, 60 * 60);

      if (error) {
        throw error;
      }

      return Object.fromEntries(
        (data ?? []).flatMap((item) => (item.path && item.signedUrl ? [[item.path, item.signedUrl]] : [])),
      ) as Record<string, string>;
    },
  });
};

export const useHelpMediaPreviewUrl = (storagePath: string | null) =>
  useQuery({
    queryKey: ["help_media_preview", storagePath],
    enabled: !!storagePath,
    // Bucket jest prywatny — podgląd w panelu idzie po podpisanym adresie.
    staleTime: 1000 * 60 * 30,
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from(HELP_MEDIA_BUCKET)
        .createSignedUrl(storagePath as string, 60 * 60);

      if (error) {
        throw error;
      }

      return data.signedUrl;
    },
  });

/**
 * Pliki, do których nie odwołuje się żaden materiał. Bez tego widoku bucket
 * zarasta sierotami z porzuconych szkiców — dokładnie jak przy nagraniach
 * klientek (`orphaned-video-uploads`).
 */
export const findUnusedHelpMedia = (media: HelpMediaEntity[], articles: HelpArticleEntity[]): HelpMediaEntity[] => {
  const usedContent = articles.map((article) => JSON.stringify(article.content ?? "")).join(" ");
  return media.filter((file) => !usedContent.includes(file.storagePath));
};

export const HELP_ANNOUNCEMENT_TARGET_KEY = ["help_announcement_target"] as const;

/**
 * Bieżące kryteria ogłoszenia. Okno „Ogłoś ponownie” startuje od nich, a nie od
 * pustych list: domyślki zamieniały pilotaż dla jednego tagu w ogłoszenie (i
 * push) do całej bazy, jeśli redakcja po prostu kliknęła „Ogłoś”.
 */
export const useHelpAnnouncementTarget = (articleId: string | null) =>
  useQuery({
    queryKey: [...HELP_ANNOUNCEMENT_TARGET_KEY, articleId],
    enabled: !!articleId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("help_announcement_target")
        .select("productIds, productTypes, tags, tagsCondition")
        .eq("articleId", articleId as string)
        .maybeSingle();

      if (error) {
        throw error;
      }

      return data;
    },
  });

export const HELP_ANNOUNCEMENT_STATS_KEY = ["help_announcement_stats"] as const;

/**
 * Ogłoszenie materiału, który jest już opublikowany — czyli „Ogłoś ponownie”
 * bez przechodzenia przez cofnięcie publikacji i publikację od nowa.
 */
export const useAnnounceHelpArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ article, announce }: { article: HelpArticleEntity; announce: HelpAnnounceOptions }) => {
      await announceHelpArticle(article.id, announce, { publish: false });

      if (announce.push) {
        // Nieudany push nie może cofnąć ogłoszenia, które już siedzi w bazie —
        // popup i tak dojdzie.
        try {
          await notificationsApi.post("/help/announce-push", {
            articleId: article.id,
            audience: announce.pushAudience,
          });
        } catch (pushError) {
          void message.warning(
            `Ogłoszenie zapisane, ale push nie poszedł: ${
              pushError instanceof Error ? pushError.message : "nieznany błąd"
            }`,
          );
        }
      }
    },
    onSuccess: async (_result, variables) => {
      void message.success("Materiał ogłoszony klientkom.");
      analyticsService.track("help_article_announced", {
        slug: variables.article.slug,
        push: variables.announce.push,
        pushAudience: variables.announce.pushAudience,
        visibleForDays: variables.announce.visibleForDays,
        again: true,
      });
      await queryClient.invalidateQueries({ queryKey: HELP_ARTICLES_KEY });
      await queryClient.invalidateQueries({ queryKey: HELP_ANNOUNCEMENT_STATS_KEY });
      await queryClient.invalidateQueries({ queryKey: HELP_ANNOUNCEMENT_TARGET_KEY });
    },
    onError: (error) => reportError(error, "Nie udało się ogłosić materiału."),
  });
};

/**
 * Dosłanie pusha tym, którzy w bieżącej rundzie ogłoszenia nic nie dostali
 * (TRE-2259). Kto ma już wydanie, dostaje zwykły push, a reszta prośbę
 * o aktualizację. Serwis pilnuje, żeby nikt nie dostał pusha drugi raz.
 */
export const useResendHelpAnnouncementPush = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (article: HelpArticleEntity) => {
      const { data } = await notificationsApi.post<{ recipients: number; updateRecipients: number }>(
        "/help/announce-push",
        { articleId: article.id, audience: "all", resend: true },
      );
      return data;
    },
    onSuccess: async (result, article) => {
      const sent = (result?.recipients ?? 0) + (result?.updateRecipients ?? 0);
      void message.success(sent ? `Wysłano push do ${sent} klientek.` : "Nie było do kogo dosłać pusha.");
      analyticsService.track("help_announcement_push_resent", { slug: article.slug, sent });
      await queryClient.invalidateQueries({ queryKey: HELP_ARTICLES_KEY });
    },
    onError: (error) => reportError(error, "Nie udało się dosłać pusha."),
  });
};

export interface HelpAnnouncementStats {
  shown: number;
  clicked: number;
}

/**
 * Zasięg ogłoszeń: ile klientek zobaczyło popup i ile weszło we wpis.
 * Liczy baza (`help_announcement_stats`), bo PostgREST ścina odpowiedź na 1000
 * wierszach — sumowanie w panelu zaniżyłoby każdy większy zasięg.
 */
export const useHelpAnnouncementStats = (articleIds: string[]) =>
  useQuery({
    queryKey: [...HELP_ANNOUNCEMENT_STATS_KEY, articleIds.join(",")],
    enabled: articleIds.length > 0,
    queryFn: async (): Promise<Record<string, HelpAnnouncementStats>> => {
      const { data, error } = await supabase.rpc("help_announcement_stats", { p_article_ids: articleIds });

      if (error) {
        throw error;
      }

      return Object.fromEntries(
        (data ?? []).map((row) => [row.articleId, { shown: Number(row.shown), clicked: Number(row.clicked) }]),
      );
    },
  });
